import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import {
  addPMIntervention,
  updatePMIntervention,
  deletePMIntervention,
} from "../../../actions/admin";
import {
  toDateLocal,
  parseToLocalFixedHour,
} from "../../../utils/toDateTimeLocal";
import DatePicker from "../../layouts/DatePicker";

// props:
// - isOpen: optional, controls expansion
// - onStartPlacement: function() -> toggles placement mode in chart
// - interventions: array of { id, time, actionText, note, provisional }
// - resultIDs: array of ResultID values to link the intervention to
// - onSaveIntervention(id, { actionText, note }) => save locally
// - onCancelProvisional(id) => remove provisional
// - onDeleteIntervention(id)
const PMInterventionActionPanel = ({
  isOpen: propOpen,
  placementMode = false,
  selectedInterventionId = null,
  onStartPlacement,
  onStartPlacementToggle,
  interventions = [],
  resultIDs = [],
  onSaveIntervention,
  onCancelProvisional,
  onDeleteIntervention,
  onCancelPlacement,
  onClearSelection,
  onSelectIntervention,
}) => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(
    typeof propOpen === "boolean" ? propOpen : true,
  );
  useEffect(() => {
    if (typeof propOpen === "boolean") setIsExpanded(propOpen);
  }, [propOpen]);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [interventionToDelete, setInterventionToDelete] = useState(null);

  // editor state for selected or provisional item
  const provisional = interventions.find((i) => i && i.provisional);
  const selected = selectedInterventionId
    ? interventions.find((i) => i && i.id === selectedInterventionId)
    : null;
  const editingTarget = selected || provisional || null;
  const [actionText, setActionText] = useState(
    editingTarget ? editingTarget.actionText || "" : "",
  );
  const [noteText, setNoteText] = useState(
    editingTarget ? editingTarget.note || "" : "",
  );
  const [dateInput, setDateInput] = useState(
    editingTarget && editingTarget.time
      ? toDateLocal(new Date(editingTarget.time))
      : "",
  );

  useEffect(() => {
    const t = editingTarget;
    if (t) {
      setActionText(t.actionText || "");
      setNoteText(t.note || "");
      setDateInput(t.time ? toDateLocal(new Date(t.time)) : "");
    } else {
      setActionText("");
      setNoteText("");
      setDateInput("");
    }
  }, [editingTarget]);

  const handleAddIntervention = async () => {
    // compute new timestamp from dateInput if provided
    let newTime = editingTarget.time;
    if (dateInput && dateInput.length) {
      const parsed = parseToLocalFixedHour(dateInput, 19);
      if (parsed) newTime = parsed;
    }

    // Format date for API (SQL Date expects YYYY-MM-DD format)
    const interventionDate = new Date(newTime);
    const formattedDate = interventionDate.toISOString().split("T")[0];

    const formData = {
      interventionTime: formattedDate,
      interventionAction: actionText || "Intervention",
      interventionNotes: noteText || "",
    };

    try {
      let finalId = editingTarget.id; // Fallback to current ID for updates

      if (editingTarget.provisional) {
        // NEW INTERVENTION: Create via API
        if (!resultIDs || resultIDs.length === 0) {
          alert("No results available to link intervention to.");
          return;
        }

        formData.resultID = resultIDs;

        // Dispatch action and catch the server payload response
        const response = await dispatch(addPMIntervention(formData));
        // Map the server-generated primary key (e.g., InterventionID or id)
        if (response && (response.interventionDetailID || response.id)) {
          finalId = response.interventionDetailID || response.id;
        }
      } else {
        // EXISTING INTERVENTION: Update via API
        await dispatch(updatePMIntervention(editingTarget.id, formData));
      }

      // Update local and chart states using the verified database ID
      if (onSaveIntervention) {
        onSaveIntervention(editingTarget.id, {
          id: finalId, // Pass the explicit backend ID to swap out the provisional ID
          actionText: actionText || "Intervention",
          note: noteText || "",
          time: newTime,
        });
      }

      onClearSelection && onClearSelection();
    } catch (error) {
      console.error("Failed to save intervention:", error);
      alert("Failed to save intervention. Please try again.");
      return;
    }
  };

  return (
    <div
      style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}
    >
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "6px",
          boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.15)",
          width: "280px",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 14px",
            borderBottom: "1px solid #DEE2E6",
            backgroundColor: "#FFFFFF",
          }}
        >
          <div
            style={{
              fontFamily:
                "Montserrat, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
              fontSize: "14px",
              fontWeight: 600,
              color: "#18A587",
            }}
          >
            Intervention Action
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {!editingTarget ? (
              <button
                title={
                  placementMode ? "Exit placement mode" : "Place intervention"
                }
                onClick={() => {
                  if (onStartPlacementToggle) onStartPlacementToggle();
                  else if (onStartPlacement) onStartPlacement();
                }}
                style={{
                  background: placementMode ? "#f28b20" : "#18A587",
                  border: "none",
                  color: "#fff",
                  borderRadius: 6,
                  padding: "6px 8px",
                  cursor: "pointer",
                }}
              >
                {placementMode ? "•" : "+"}
              </button>
            ) : null}

            <button
              onClick={() => setIsExpanded(!isExpanded)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="18px"
                viewBox="0 -960 960 960"
                width="18px"
                fill="#18A587"
                style={{
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              >
                <path d="M531-156 297-390l45-45 189 189 189-189 45 45-234 234Z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        {isExpanded && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              padding: "12px 14px 14px",
            }}
          >
            {/* Placement mode instructions or editor or list */}
            {placementMode ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ fontSize: 13, color: "#333" }}>
                  Click on the chart to place an intervention marker.
                </div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  Press Esc or click Cancel to exit placement mode.
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                  }}
                >
                  <button
                    onClick={() => onCancelPlacement && onCancelPlacement()}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 6,
                      border: "1px solid #ccc",
                      background: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : editingTarget ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <input
                  value={actionText}
                  onChange={(e) => setActionText(e.target.value)}
                  placeholder="Action summary (e.g. Small group instruction)"
                  style={{
                    padding: "8px",
                    borderRadius: 6,
                    border: "1px solid #CED4DA",
                  }}
                />
                <DatePicker
                  type="date"
                  value={dateInput}
                  onChange={setDateInput}
                  useBootstrap={false}
                />
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Optional note"
                  style={{
                    padding: "8px",
                    borderRadius: 6,
                    border: "1px solid #CED4DA",
                    minHeight: 60,
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => {
                      // cancel editing
                      if (editingTarget.provisional) {
                        onCancelProvisional &&
                          onCancelProvisional(editingTarget.id);
                      }
                      onClearSelection && onClearSelection();
                    }}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 6,
                      border: "1px solid #ccc",
                      background: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddIntervention}
                    onKeyDown={async (e) => {
                      // allow Enter to submit when focused
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddIntervention();
                      }
                    }}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 6,
                      border: "none",
                      background: "#18A587",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    OK
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "#333" }}>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>
                  Recent interventions
                </div>
                {interventions &&
                interventions.filter((i) => !i.provisional).length > 0 ? (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    {interventions
                      .filter((i) => !i.provisional)
                      .sort((a, b) => a.time - b.time)
                      .map((it) => (
                        <div
                          key={it.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 8,
                            alignItems: "center",
                          }}
                        >
                          <div style={{ fontSize: 13 }}>
                            <div style={{ fontWeight: 600 }}>
                              {it.actionText || "Intervention"}
                            </div>
                            <div style={{ fontSize: 12, color: "#666" }}>
                              {new Date(it.time).toLocaleDateString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="18"
                              width="18"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              role="button"
                              tabIndex={0}
                              title="Edit"
                              aria-label="Edit intervention"
                              onClick={() =>
                                onSelectIntervention &&
                                onSelectIntervention(it.id)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  onSelectIntervention &&
                                    onSelectIntervention(it.id);
                                }
                              }}
                              className="cursor-pointer pm-icon pm-icon--edit"
                            >
                              <path d="M0 0h24v24H0V0z" fill="none" />
                              <path d="M3 17.46v3.04c0 .28.22.5.5.5h3.04c.13 0 .26-.05.35-.15L17.81 9.94l-3.75-3.75L3.15 17.1c-.1.1-.15.22-.15.36zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                            </svg>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="currentColor"
                              role="button"
                              tabIndex={0}
                              title="Delete"
                              aria-label="Delete intervention"
                              onClick={() => {
                                setInterventionToDelete(it);
                                setShowDeleteModal(true);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setInterventionToDelete(it);
                                  setShowDeleteModal(true);
                                }
                              }}
                              className="cursor-pointer pm-icon--delete"
                            >
                              <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1H2.5zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5zM8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5zm3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0z" />
                            </svg>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div style={{ color: "#666", fontSize: 13 }}>
                    No interventions yet.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Intervention</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this intervention?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              try {
                if (interventionToDelete) {
                  // Only call API if intervention exists in DB (not provisional)
                  if (!interventionToDelete.provisional) {
                    await dispatch(
                      deletePMIntervention(interventionToDelete.id),
                    );
                  }
                  // Update local state
                  onDeleteIntervention &&
                    onDeleteIntervention(interventionToDelete.id);
                  onClearSelection && onClearSelection();
                }
                setShowDeleteModal(false);
                setInterventionToDelete(null);
              } catch (error) {
                console.error("Failed to delete intervention:", error);
                alert("Failed to delete intervention. Please try again.");
                setShowDeleteModal(false);
              }
            }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PMInterventionActionPanel;
